import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  MagnifyingGlass,
  CalendarBlank,
  Bank,
  CaretLeft,
  CaretRight,
  Warning,
  PencilSimple,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import ExportDropdown from "../../export";
import BankUpdateModal from "../../modals/BankUpdateModal";
import { useGetPaymentAdvice } from "../../../adapters/Requests";
import { DateRangeFilter } from "../../ui/daterangefilter";

export default function PaymentAdviceTable() {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState([]);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [selectedIntern, setSelectedIntern] = useState(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const {
    data: apiResponse,
    isLoading,
    refetch,
  } = useGetPaymentAdvice({
    month: selectedMonth,
    year: selectedYear,
    start_date: dateRange.from,
    end_date: dateRange.to,
  });

  useEffect(() => {
    if (apiResponse?.data) {
      setTableData(apiResponse.data);
    }
  }, [apiResponse]);

  const handlePaymentChange = (rowId, newType) => {
    setTableData((currentData) =>
      currentData.map((item) => {
        if (item.id === rowId) {
          let newAmount = 0;
          if (newType === "FULL") newAmount = item.baseStipend;
          if (newType === "HALF") newAmount = item.baseStipend / 2;
          if (newType === "NONE") newAmount = 0;

          return {
            ...item,
            suggestedPaymentType: newType,
            amount: newAmount,
          };
        }
        return item;
      }),
    );
  };

  const openBankModal = (intern) => {
    setSelectedIntern(intern);
    setIsBankModalOpen(true);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData;
    return tableData.filter(
      (item) =>
        item.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.staffId.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [tableData, searchTerm]);

  const columns = [
    {
      header: "Staff ID",
      accessorKey: "staffId",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-slate-500">
          {row.original.staffId}
        </span>
      ),
    },
    {
      header: "Beneficiary Name",
      accessorKey: "fullName",
      cell: ({ row }) => (
        <span className="font-semibold text-sm text-slate-900">
          {row.original.fullName}
        </span>
      ),
    },
    {
      header: "Bank Details",
      accessorKey: "bankName",
      cell: ({ row }) => {
        const hasFlag = row.original.supervisorComment?.includes(
          "[URGENT: BANK DETAILS UPDATE REQUESTED]",
        );

        return (
          <div className="flex items-start justify-between gap-2 group">
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-slate-700 flex items-center">
                <Bank className="mr-1" size={12} /> {row.original.bankName}
              </span>
              <span className="text-[10px] text-slate-500">
                {row.original.accountNumber}
              </span>
            </div>

            {hasFlag ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-red-500 bg-red-50 hover:bg-red-100 animate-pulse"
                onClick={() => openBankModal(row.original)}
                title="Supervisor Requested Bank Update"
              >
                <Warning size={14} weight="fill" />
              </Button>
            ) : (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-green-600 transition-all"
                onClick={() => openBankModal(row.original)}
                title="Edit Bank Details"
              >
                <PencilSimple size={14} />
              </Button>
            )}
          </div>
        );
      },
    },
    {
      header: "Attendance",
      accessorKey: "daysPresent",
      cell: ({ row }) => (
        <div className="text-xs text-center">
          <span
            className={`font-bold ${
              row.original.suggestedPaymentType === "HALF"
                ? "text-orange-600"
                : "text-slate-900"
            }`}
          >
            {row.original.daysPresent}
          </span>
          <span className="text-slate-400">/{row.original.totalDays}</span>
        </div>
      ),
    },
    {
      header: "Payment Action",
      id: "actions",
      cell: ({ row }) => (
        <Select
          value={row.original.suggestedPaymentType}
          onValueChange={(value) => handlePaymentChange(row.original.id, value)}
        >
          <SelectTrigger className="w-[100px] h-7 text-xs">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="FULL">Full Pay</SelectItem>
            <SelectItem value="HALF">Half Pay</SelectItem>
            <SelectItem value="NONE">None</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      header: "Amount",
      accessorKey: "amount",
      cell: ({ row }) => (
        <span className="font-bold text-slate-900">
          ₦{row.original.amount.toLocaleString()}
        </span>
      ),
    },
  ];

  const exportColumns = [
    { headerName: "Staff ID", field: "staffId" },
    { headerName: "Beneficiary Name", field: "fullName" },
    { headerName: "Beneficiary Account", field: "accountNumber" },
    { headerName: "Bank Code", field: "sortCode" },
    { headerName: "Bank Name", field: "bankName" },
    {
      headerName: "Amount (NGN)",
      field: "amount",
      formatter: (value) => Number(value ?? 0).toLocaleString(),
    },
    { headerName: "Narration", field: "narration" },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination },
    onPaginationChange: setPagination,
  });

  return (
    <div className="w-full px-6">
      <CardHeader className="flex flex-col sm:flex-row justify-between gap-4 p-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex gap-2 items-center flex-wrap">
          <DateRangeFilter
            dateRange={dateRange}
            setDateRange={setDateRange}
            minDate={apiResponse?.meta?.minDate}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="bg-white h-8 text-xs">
                <CalendarBlank className="mr-2 h-3 w-3" />
                {months[selectedMonth - 1]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="h-64 overflow-y-auto" align="start">
              {months.map((m, i) => (
                <DropdownMenuItem
                  key={m}
                  onClick={() => setSelectedMonth(i + 1)}
                >
                  {m}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center px-3 border rounded-md bg-white text-xs h-8">
            {selectedYear}
          </div>
        </div>

        <div className="flex gap-3">
          <div className="relative">
            <MagnifyingGlass
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={14}
            />
            <Input
              placeholder="Search beneficiary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-white w-64 h-8 text-xs"
            />
          </div>
          <ExportDropdown
            data={filteredData}
            columns={exportColumns}
            className="h-8"
          />
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="text-[11px] uppercase font-bold text-slate-500 h-9"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-48 text-center"
                  >
                    <Loader2 className="w-8 h-8 text-green-600 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-slate-50 border-b border-slate-100"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-32 text-center text-slate-500 text-xs"
                  >
                    No approved payment advice found for this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
        <div className="text-[11px] text-gray-500">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            <CaretLeft size={12} className="mr-1" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            Next <CaretRight size={12} className="ml-1" />
          </Button>
        </div>
      </div>

      {selectedIntern && (
        <BankUpdateModal
          isOpen={isBankModalOpen}
          onClose={() => setIsBankModalOpen(false)}
          intern={selectedIntern}
          onSuccess={refetch}
        />
      )}
    </div>
  );
}
